import type { FC } from 'react';
import { Button } from '@components/ui/button';
import type { Comparator } from '@domain/types/filters';
import type { ResolvedSchemaTree } from '@root/helpers/schema/types';
import clsx from 'clsx';
import { Trash2 } from 'lucide-react';

import { ALL_COMPARATORS, FIELD_DEF_MAP } from '../constants';
import type { FieldDef, FilterRow, OptionsRecord } from '../types';

import { FieldSelector } from './fieldSelector/FieldSelector';
import { ComparatorSelector } from './ComparatorSelector';
import { ValueInput } from './ValueInput';

interface QueryBuilderRowProps {
  row: FilterRow;
  contractDetailsFields: FieldDef[];
  blockFields: FieldDef[];
  datapointTree: ResolvedSchemaTree[];
  datapointFieldDefMap: Map<string, FieldDef>;
  optionsRecord: OptionsRecord;
  onRowUpdate: (rowId: string, patch: Partial<FilterRow>) => void;
  onRemove: (rowId: string) => void;
}

export const QueryBuilderRow: FC<QueryBuilderRowProps> = ({
  row,
  contractDetailsFields,
  blockFields,
  datapointTree,
  datapointFieldDefMap,
  optionsRecord,
  onRowUpdate,
  onRemove,
}) => {
  const fieldDef = row.field ? (FIELD_DEF_MAP.get(row.field) ?? datapointFieldDefMap.get(row.field)) : undefined;

  const isFlagField = fieldDef?.valueInput === 'FlagSelect';
  const comparators: Comparator[] = fieldDef?.comparators ?? ALL_COMPARATORS;

  return (
    <div
      className="grid w-full shrink-0 grid-cols-[minmax(0,1fr)_10rem_minmax(0,1fr)_auto] items-center gap-4"
      data-testid="query-builder-row"
    >
      <div className="min-w-0" data-testid="query-builder-field">
        <FieldSelector
          contractDetailsFields={contractDetailsFields}
          blockFields={blockFields}
          datapointTree={datapointTree}
          value={row.field}
          onChange={(fieldId) => {
            const def = FIELD_DEF_MAP.get(fieldId) ?? datapointFieldDefMap.get(fieldId);
            onRowUpdate(row.id, { field: fieldId, comparator: def?.comparators[0] ?? '', values: [] });
          }}
        />
      </div>

      {!isFlagField && (
        <ComparatorSelector
          comparators={comparators}
          value={row.comparator}
          onChange={(c) => onRowUpdate(row.id, { comparator: c })}
          disabled={!row.field}
        />
      )}

      {/* Flag fields have no comparator; the value select absorbs its column. */}
      <div className={clsx('min-w-0', isFlagField && 'col-span-2')} data-testid="query-builder-value">
        <ValueInput
          field={row.field}
          comparator={row.comparator}
          values={row.values}
          optionsRecord={optionsRecord}
          datapointFieldDef={fieldDef}
          onUpdate={(patch) => onRowUpdate(row.id, patch)}
        />
      </div>

      <Button variant="ghost" size="icon" aria-label="Remove filter" onClick={() => onRemove(row.id)}>
        <Trash2 className="size-5" />
      </Button>
    </div>
  );
};
